import type { SupabaseClient } from '@supabase/supabase-js'
import {
  claimTaskResultSchema,
  disconnectResultSchema,
  heartbeatResultSchema,
  helpAvailabilityResultSchema,
  itemCommandResultSchema,
  missionHeaderResultSchema,
  missionTrackSchema,
  operatorMissionHomeSchema,
  operatorHelpRequestsSchema,
  operatorMissionViewSchema,
  operatorReinforcementOffersSchema,
  operatorRoutePlanSchema,
  pauseResumeResultSchema,
  positionsAckSchema,
  sendHelpRequestResultSchema,
  startMissionResultSchema,
  startReinforcementResultSchema,
} from '@groupe-reca/contracts'
import { callRpc } from '@/infrastructure/supabase/client'
import type { MissionRepository } from '../../application/MissionRepository'

export function createSupabaseMissionRepository(client: SupabaseClient): MissionRepository {
  const command = (name: string, payload: object) => callRpc(client, name, { p_command: payload })
  return {
    async home() {
      return operatorMissionHomeSchema.parse(await callRpc(client, 'operator_mission_home_v1'))
    },
    async routePlan() {
      return operatorRoutePlanSchema.parse(await callRpc(client, 'operator_route_plan_v1'))
    },
    async start(payload) {
      return startMissionResultSchema.parse(await command('start_operator_mission_v1', payload))
    },
    async view(missionId) {
      return operatorMissionViewSchema.parse(await callRpc(client, 'operator_mission_view_v1', { p_mission_id: missionId }))
    },
    async complete(payload) {
      return itemCommandResultSchema.parse(await command('complete_mission_item_v1', payload))
    },
    async reportProblem(payload) {
      return itemCommandResultSchema.parse(await command('report_mission_item_problem_v1', payload))
    },
    async pause(payload) {
      return pauseResumeResultSchema.parse(await command('pause_operator_mission_v1', payload))
    },
    async resume(payload) {
      return pauseResumeResultSchema.parse(await command('resume_operator_mission_v1', payload))
    },
    async end(payload) {
      return missionHeaderResultSchema.parse(await command('end_operator_mission_v1', payload))
    },
    async sendPositions(missionId, points) {
      return positionsAckSchema.parse(await command('record_mission_positions_v1', { missionId, points }))
    },
    async sendGap(gap) {
      await command('record_mission_gps_gap_v1', gap)
    },
    async track(missionId) {
      return missionTrackSchema.parse(await callRpc(client, 'mission_track_v1', { p_mission_id: missionId }))
    },
    async cancelStart(payload) {
      return missionHeaderResultSchema.parse(await command('cancel_operator_mission_start_v1', payload))
    },
    async heartbeat(payload) {
      return heartbeatResultSchema.parse(await command('operator_heartbeat_v1', payload))
    },
    async disconnect(payload) {
      disconnectResultSchema.parse(await command('operator_disconnect_v1', payload))
    },
    async setHelpAvailability(available) {
      return helpAvailabilityResultSchema.parse(await command('set_operator_help_availability_v1', { available }))
    },
    async sendHelpRequest(payload) {
      return sendHelpRequestResultSchema.parse(await command('send_help_request_v1', payload))
    },
    async helpRequests() {
      return operatorHelpRequestsSchema.parse(await callRpc(client, 'operator_help_requests_v1'))
    },
    async reinforcementOffers() {
      return operatorReinforcementOffersSchema.parse(await callRpc(client, 'operator_reinforcement_offers_v1'))
    },
    async claimTask(payload) {
      return claimTaskResultSchema.parse(await command('claim_reinforcement_task_v1', payload))
    },
    async startReinforcement(payload) {
      return startReinforcementResultSchema.parse(await command('start_reinforcement_mission_v1', payload))
    },
  }
}
